/**
 * MediaUnitDetailsSchema
 *
 * Shared parser for the `mediaUnitDetails` JSONB field stored in offer snapshots.
 * Used by both the authenticated client-offer path and the public-offer path so
 * a single schema change covers both mappers.
 */
import { z } from 'zod';

/**
 * Replaces every `null` own-property value in a plain object with
 * `undefined` before validation. The API's `buildMediaUnitDetails` writes
 * `null` for any unfilled source column (offer-versions.service.ts), but
 * declaring the fields below as `.nullable()` (instead of preprocessing)
 * would give the object schema's inferred `Output` type per-field
 * `ZodEffects` members, which breaks TypeScript's narrowing of the common
 * `safeParse(...).data ?? {}` call-site pattern (`Property 'x' does not
 * exist on type '{}'`) even though the runtime value is correct. Stripping
 * nulls in a preprocess step keeps every field a plain `z.string().optional()`
 * so `Output` stays a simple object type. Non-object input passes through
 * unchanged so the wrapped schema still fails validation on genuinely
 * malformed input.
 *
 * MUST NOT replace the field schemas below with a bare `.nullable()`
 * variant: a single `null` field failing its own field schema fails the
 * whole `z.object`, and every call site does `safeParse(...).data ?? {}`,
 * so one missing field would silently drop the entire unit's
 * already-populated data from the client view (CU-86cb4wjwu).
 */
function stripNullOwnProperties(input: unknown): unknown {
  if (input == null || typeof input !== 'object' || Array.isArray(input)) return input;
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    result[key] = value === null ? undefined : value;
  }
  return result;
}

export const MediaUnitDetailsSchema = z.preprocess(
  stripNullOwnProperties,
  z.object({
    supplier: z.string().optional(),
    /** Present only when the caller was granted supplier identity server-side (see OQ-5/Decision 6). */
    supplierNumber: z.string().optional(),
    format: z.string().optional(),
    city: z.string().optional(),
    address: z.string().optional(),
    /** RW Group's own media-unit numbering — the client-facing identifier. */
    grwId: z.string().optional(),
    voivodeship: z.string().optional(),
    /** Formatted "{width} x {height} m" fallback — see offer-versions.service.ts buildMediaUnitDetails. */
    dimensions: z.string().optional(),
  }),
);
